import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { KeyboardControls } from "@react-three/drei";
import { RigidBody, CuboidCollider } from "@react-three/rapier";
import Ecctrl from "ecctrl";
import type { RapierRigidBody } from "@react-three/rapier";
import { LOBBY_TICK_HZ } from "@solblox/shared";
import type { Vec3 } from "@solblox/shared";

import { sendMove } from "../net/colyseus";
import { useStore } from "../store";
import { useIdentity } from "../identity";
import { SpectatorCamera } from "./SpectatorCamera";
import { avatarMaterialProps, resolveSkinColor } from "../lobby/skinColor";
import { saveSpawn } from "./playerSpawn";

const SEND_INTERVAL_MS = 1000 / LOBBY_TICK_HZ;
// localStorage writes are sync + can jank; persist position at most ~1×/sec.
const SAVE_INTERVAL_MS = 1000;

// Idle dead-zone. The rapier body never truly rests: ecctrl's float-spring
// rings in Y and the capsule micro-drifts in XZ, so a "motionless" player still
// emits a stream of sub-centimeter deltas every frame. Broadcasting those is
// exactly what makes every remote avatar twitch/bob forever. We only emit a move
// when a component actually crossed these thresholds; below them we hold the
// last-sent pose, so a standing player is dead still on every other screen.
const POS_EPS = 0.02; // metres, horizontal drift
const Y_EPS = 0.08; // metres — idle bob is well under this, a real jump is well over
const YAW_EPS = 0.04; // radians (~2.3°) — autoBalance yaw ring is well under this

// Throne sits at the world origin (THRONE_POSITION = [0,0,0]). The local player
// can claim it (F key) only within this horizontal radius; hysteresis keeps the
// prompt from flickering when standing right on the boundary.
const THRONE_TOUCH_RADIUS = 18; // metres
const THRONE_TOUCH_HYSTERESIS = 3; // metres of sticky exit

function wrapAngle(a: number): number {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

const keyboardMap = [
  { name: "forward", keys: ["ArrowUp", "KeyW"] },
  { name: "backward", keys: ["ArrowDown", "KeyS"] },
  { name: "leftward", keys: ["ArrowLeft", "KeyA"] },
  { name: "rightward", keys: ["ArrowRight", "KeyD"] },
  { name: "jump", keys: ["Space"] },
  { name: "run", keys: ["Shift"] },
];

interface ControlsEcctrlProps {
  spawn: Vec3;
}

function PositionBroadcaster({
  bodyRef,
}: {
  bodyRef: React.MutableRefObject<RapierRigidBody | null>;
}): null {
  const lastSendRef = useRef(0);
  const lastSaveRef = useRef(0);
  // Last pose actually broadcast — the dead-zone compares against this so we
  // only refresh the components that genuinely moved.
  const lastSentRef = useRef<{ x: number; y: number; z: number; rotY: number } | null>(null);
  // Latest known horizontal position, kept fresh every frame so the tab-hide /
  // refresh flush below can persist the exact final spot (not the throttled one
  // from up to a second ago).
  const latestRef = useRef<{ x: number; z: number } | null>(null);
  // Sticky throne-proximity flag (mirrors store.nearThrone; only writes on flip).
  const nearThroneRef = useRef(false);
  // Clear the flag when the rig unmounts (leaving the lobby) so the hotkey/prompt
  // can't linger on a stale "in range".
  useEffect(() => () => useStore.getState().setNearThrone(false), []);

  useFrame(() => {
    const body = bodyRef.current;
    if (!body) return;
    const now = performance.now();
    const t = body.translation();
    latestRef.current = { x: t.x, z: t.z };

    // Throne proximity → gates the F-to-touch hotkey + on-screen prompt. Crisp
    // enter, sticky exit. Store-backed so the DOM HUD (outside the canvas) reads it.
    const distToThrone = Math.hypot(t.x, t.z);
    const near = nearThroneRef.current
      ? distToThrone < THRONE_TOUCH_RADIUS + THRONE_TOUCH_HYSTERESIS
      : distToThrone < THRONE_TOUCH_RADIUS;
    if (near !== nearThroneRef.current) {
      nearThroneRef.current = near;
      useStore.getState().setNearThrone(near);
    }

    if (now - lastSaveRef.current >= SAVE_INTERVAL_MS) {
      lastSaveRef.current = now;
      saveSpawn({ x: t.x, z: t.z });
    }

    if (now - lastSendRef.current < SEND_INTERVAL_MS) return;
    const room = useStore.getState().currentRoom;
    if (!room) return;
    const r = body.rotation();
    const sinY = 2 * (r.w * r.y + r.x * r.z);
    const cosY = 1 - 2 * (r.y * r.y + r.x * r.x);
    const yaw = Math.atan2(sinY, cosY);

    const last = lastSentRef.current;
    if (!last) {
      lastSendRef.current = now;
      lastSentRef.current = { x: t.x, y: t.y, z: t.z, rotY: yaw };
      sendMove({ x: t.x, y: t.y, z: t.z, rotY: yaw });
      return;
    }
    // Refresh only the components that crossed the dead-zone. Idle float-spring
    // ring (Y) and physics micro-drift (XZ) stay below threshold and never ship,
    // so remote avatars hold perfectly still until the player really moves.
    let { x: sx, y: sy, z: sz, rotY: sr } = last;
    let moved = false;
    if (Math.abs(t.x - last.x) > POS_EPS || Math.abs(t.z - last.z) > POS_EPS) {
      sx = t.x;
      sz = t.z;
      moved = true;
    }
    if (Math.abs(t.y - last.y) > Y_EPS) {
      sy = t.y;
      moved = true;
    }
    if (Math.abs(wrapAngle(yaw - last.rotY)) > YAW_EPS) {
      sr = yaw;
      moved = true;
    }
    // Nothing real changed → stay silent. We deliberately don't advance
    // lastSendRef here, so the instant the player moves again it broadcasts on
    // the very next frame with zero added latency.
    if (!moved) return;
    lastSendRef.current = now;
    lastSentRef.current = { x: sx, y: sy, z: sz, rotY: sr };
    sendMove({ x: sx, y: sy, z: sz, rotY: sr });
  });

  // Capture the final position the instant the tab is backgrounded or the page
  // is being torn down (refresh / close), so a reload lands exactly where the
  // player was rather than up to SAVE_INTERVAL_MS behind.
  useEffect(() => {
    const flush = (): void => {
      if (latestRef.current) saveSpawn(latestRef.current);
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
}

/**
 * Safety net: if the capsule ends up below the world (a physics blow-up, a
 * stray teleport, a delta spike that slipped past TabRefocusGuard), snap it
 * back to spawn instead of stranding the player in the dark void with no
 * recourse but a page refresh.
 */
function FallGuard({
  bodyRef,
  spawn,
}: {
  bodyRef: React.MutableRefObject<RapierRigidBody | null>;
  spawn: Vec3;
}): null {
  useFrame(() => {
    const body = bodyRef.current;
    if (!body) return;
    const t = body.translation();
    // World floor top sits at y=0; anything past -15 has fallen off the map.
    if (t.y < -15) {
      body.setTranslation({ x: spawn.x, y: spawn.y + 2, z: spawn.z }, true);
      body.setLinvel({ x: 0, y: 0, z: 0 }, true);
      body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    }
  });
  return null;
}

/**
 * Crab-Game-style movement spike: ecctrl floating-capsule controller on top of
 * a rapier physics world. Wraps a placeholder capsule mesh — character-model
 * swap to Quaternius UAL is a follow-up.
 *
 * Mounts a giant static cuboid as the world floor so the capsule has something
 * to stand on; the visual room geometry from RoomLoader still renders on top.
 */
export function ControlsEcctrl({ spawn }: ControlsEcctrlProps): JSX.Element {
  const bodyRef = useRef<RapierRigidBody | null>(null);
  const isSpectator = useStore((s) => s.localPlayer?.isSpectator === true);
  // When the admin cinematic orbit is on, hand the camera to <AerialCamera> and
  // freeze input so the streamer can't drift the avatar mid-shot.
  const aerial = useStore((s) => s.aerialView);
  // Read the optimistic local pick first; fall back to the server-confirmed
  // value on the Player schema. Without this the capsule waits a full
  // server roundtrip before reflecting a pick, which reads as a broken UI
  // (the SkinPicker toggle updates immediately, but the capsule lags).
  const localColor = useStore(
    (s) => s.skinColorHex ?? s.localPlayer?.colorHex ?? "",
  );
  // Stable identity for the derived-color fallback: wallet pubkey if connected,
  // else the persistent guest id (localStorage) — never the Colyseus sessionId
  // (s.localPlayer.id), which is fresh per connection and would reshuffle the
  // color on every reload.
  const { guest, wallet } = useIdentity();
  const localIdentity = wallet.pubkey || guest.id;
  const resolvedColor = resolveSkinColor(localColor, localIdentity);
  // Freeze the initial spawn position so re-renders triggered by remote-player
  // updates don't hand ecctrl a fresh array literal (which it can interpret as
  // a teleport request and re-seed the body mid-frame).
  const initialPosition = useMemo<[number, number, number]>(
    () => [spawn.x, spawn.y + 1, spawn.z],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Spectators get a free-cam over the same hall — no rigid body, no input.
  if (isSpectator) {
    return <SpectatorCamera target={spawn} />;
  }

  return (
    <>
      <RigidBody type="fixed" colliders={false} position={[0, -0.5, 0]}>
        <CuboidCollider args={[100, 0.5, 100]} />
      </RigidBody>

      <KeyboardControls map={keyboardMap}>
        <Ecctrl
          ref={bodyRef}
          position={initialPosition}
          disableFollowCam={aerial}
          disableControl={aerial}
          maxVelLimit={8}
          sprintMult={2}
          jumpVel={7}
          // Acceleration smoothing. Ecctrl's default accDeltaTime=8 is a heavy
          // ramp — the per-frame move impulse is `(targetVel - currentVel)/accDeltaTime`,
          // so a big value means you ooze up to top speed (and ooze to a stop),
          // which reads as "sluggish/slow". 2.5 reaches top speed in a couple of
          // frames for a crisp Crab-Game-style start/stop without overshoot (it's
          // a first-order approach to the velocity target, so it can't ring).
          accDeltaTime={2.5}
          camInitDis={-6}
          // Spawn facing the throne (hall back at -z). Without this the player
          // spawns looking back at the entrance; a 180° yaw turns them around.
          camInitDir={{ x: 0, y: Math.PI }}
          camMaxDis={-10}
          camMinDis={-2}
          camMoveSpeed={7}
          // Float-spring damping. The float force is applied as a per-frame
          // IMPULSE: `springK*(floatDis - rayDist) - linvel.y*dampingC`. Because
          // it's an impulse (not a dt-scaled force), a large dampingC doesn't
          // gently damp — it over-corrects the velocity and pumps energy in,
          // which is the trampoline/sky-launch. So this stays small. Ecctrl's
          // default 0.08 is underdamped (idle ring); 0.14 is the most we can add
          // before the jump (which shares this term on the first grounded frame)
          // starts getting eaten. The remaining idle ring is small at a stable
          // 60fps — it only LOOKS erratic when the framerate is low, so the real
          // lever for "bobbing" is FPS, not this number. Do NOT raise past ~0.3.
          dampingC={0.14}
        >
          <mesh castShadow>
            <capsuleGeometry args={[0.3, 0.7, 4, 12]} />
            <meshStandardMaterial {...avatarMaterialProps(resolvedColor)} />
          </mesh>
        </Ecctrl>
      </KeyboardControls>

      <PositionBroadcaster bodyRef={bodyRef} />
      <FallGuard bodyRef={bodyRef} spawn={spawn} />
    </>
  );
}
